import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { HISTORY_LENGTH, OFFLINE_THRESHOLD_MS } from "../lib/constants";

/**
 * Poll the hub for the current state of every server every `tickSeconds`.
 *
 * History strategy:
 *   - The first time we see a server, fire a one-shot `/metrics?limit=N` to
 *     seed its sparkline buffers — so the chart isn't flat for the first 90 s.
 *     Fire-and-forget; doesn't block the render of the latest snapshot.
 *   - Subsequent ticks append the latest sample to the in-memory ring buffers.
 *
 * `pulses` map carries serverIds whose CPU changed by >2 % vs the previous
 * tick. The table uses it to flash newly-updated rows.
 */
export function useServers({ tickSeconds = 3 } = {}) {
  const [servers, setServers] = useState([]);
  const [pulses, setPulses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  const prevRef = useRef(new Map());
  const seededRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;

    const seedHistoryFor = (ids) => {
      if (ids.length === 0) return;
      for (const id of ids) seededRef.current.add(id);

      Promise.allSettled(
        ids.map((id) => api.serverMetrics(id, { limit: HISTORY_LENGTH })),
      ).then((results) => {
        if (cancelled) return;
        const patches = new Map();
        for (let i = 0; i < ids.length; i++) {
          const r = results[i];
          if (r.status === "fulfilled" && r.value?.points?.length > 0) {
            const pts = r.value.points;
            patches.set(ids[i], {
              historyCpu: pts.map((p) => p.cpu),
              historyRam: pts.map((p) => p.ram),
              historyDisk: pts.map((p) => p.disk),
            });
          }
        }
        if (patches.size === 0) return;
        setServers((prev) =>
          prev.map((s) => (patches.has(s.id) ? { ...s, ...patches.get(s.id) } : s)),
        );
        for (const [id, patch] of patches) {
          const cached = prevRef.current.get(id);
          if (cached) prevRef.current.set(id, { ...cached, ...patch });
        }
      });
    };

    const fetchOnce = async () => {
      try {
        const data = await api.listServers();
        if (cancelled) return;

        const t = Date.now();
        const newPulses = {};
        const toSeed = [];

        const next = data.servers.map((row) => {
          const prevEntry = prevRef.current.get(row.id);
          const offline = !row.lastSeenAt || t - row.lastSeenAt > OFFLINE_THRESHOLD_MS;
          const cpu = offline ? null : row.latestCpu;
          const ram = offline ? null : row.latestRam;
          const disk = offline ? null : row.latestDisk;
          // null means "this host reports no load average" (or is offline);
          // the panel renders "—" rather than a zero that reads as idle.
          const load = offline
            ? null
            : row.load1 == null
              ? null
              : { one: row.load1, five: row.load5, fifteen: row.load15 };

          const sampleAdvanced = row.latestTs != null && row.latestTs !== prevEntry?.latestTs;
          const append = (arr, v) =>
            sampleAdvanced && v != null ? [...(arr || []), v].slice(-HISTORY_LENGTH) : arr || [];

          const historyCpu = append(prevEntry?.historyCpu, cpu);
          const historyRam = append(prevEntry?.historyRam, ram);
          const historyDisk = append(prevEntry?.historyDisk, disk);

          if (prevEntry && cpu != null && prevEntry.cpu != null && Math.abs(cpu - prevEntry.cpu) > 2) {
            newPulses[row.id] = true;
          }

          if (!seededRef.current.has(row.id) && row.latestTs != null) {
            toSeed.push(row.id);
          }

          return {
            id: row.id,
            name: row.name,
            host: row.host,
            ip: row.host,
            port: row.port,
            tag: row.tag,
            region: row.region,
            role: row.tag,
            cpu,
            ram,
            disk,
            load,
            offline,
            // The agent reports uptime with its ~60s meta push, so the stored
            // value is up to a minute stale. Extrapolate from the moment of the
            // reading to keep the panel ticking. An offline host isn't running,
            // so its uptime is unknown rather than "still counting".
            uptimeSec:
              offline || row.uptimeSec == null
                ? null
                : row.uptimeSec + Math.max(0, t - (row.uptimeSecAt ?? t)) / 1000,
            lastSeen: row.lastSeenAt,
            latestTs: row.latestTs,
            historyCpu,
            historyRam,
            historyDisk,
            os: row.os || "—",
            kernel: row.kernel,
            arch: row.arch,
            cores: row.cores || 0,
            ramGb: row.ramGb || 0,
            diskGb: row.diskGb || 0,
            agentVersion: row.agentVersion,
            status: row.status,
            wsConnected: row.wsConnected ?? false,
          };
        });

        const map = new Map();
        for (const s of next) map.set(s.id, s);
        prevRef.current = map;

        setServers(next);
        setPulses(newPulses);
        setNow(t);
        setLoading(false);
        setError(null);

        // Kick off seed fetches after state is updated so initial render isn't
        // blocked on the history call.
        seedHistoryFor(toSeed);

        // Clear only the keys we just set instead of replacing the whole map.
        // If a future tick added pulses while this 1.4s timer was pending,
        // they're preserved (we only delete what we know we created).
        const idsToClear = Object.keys(newPulses);
        if (idsToClear.length > 0) {
          setTimeout(() => {
            if (cancelled) return;
            setPulses((p) => {
              let changed = false;
              const out = { ...p };
              for (const id of idsToClear) {
                if (id in out) {
                  delete out[id];
                  changed = true;
                }
              }
              return changed ? out : p;
            });
          }, 1400);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      }
    };

    fetchOnce();
    const id = setInterval(fetchOnce, Math.max(1, tickSeconds) * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [tickSeconds]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return { servers, pulses, loading, error, now };
}
